import type { FormikHelpers } from "formik";
import { FormikInputWithLabel } from "newui/components/ui/InputWithLabel";
import React from "react";
import { EmailField } from "./EmailField";
import * as Yup from "yup";

interface UsernamePasswordProps {
	emailDataTestid: string;
	isSignUp?: boolean;
	passwordConfirmationDataTestid?: string;
	passwordDataTestid?: string;
	setFieldValue?: FormikHelpers<any>["setFieldValue"];
}

export const createPasswordSchema = Yup.string()
	.min(8, 'Password must be at least 8 characters')
	.max(64, 'Password cannot exceed 64 characters')
	.matches(
		/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
		'Password must contain uppercase, lowercase, and number'
	)
	.matches(
		/^(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).*$/,
		'Password must contain at least one special character'
	)
	.trim()
	.test(
		'password-validation',
		'Password must not contain first name, last name, or email',
		function (passwordValue) {
			const { first_name, last_name, email } = this.parent;
			const passwordLower = passwordValue?.toLowerCase();

			const checkValue = (fieldName: string, valueToCheck?: string) => {
				if (!passwordValue || !valueToCheck) return { isValid: true, validationError: null };

				const isValid = !passwordLower.includes(valueToCheck.toLowerCase());
				return {
					isValid,
					validationError: isValid
						? null
						: this.createError({
							path: 'password',
							message: `Password must not contain your ${fieldName}`,
						}),
				};
			};

			for (const [fieldName, value] of [
				['first name', first_name],
				['last name', last_name],
				['email', email],
			]) {
				const result = checkValue(fieldName, value);
				if (!result.isValid) return result.validationError;
			}

			return true;
		}
	)
	.required('Password is required');

export const passwordConfirmationSchema = Yup.string()
	.oneOf([Yup.ref('password')], 'Passwords do not match')
	.required('Please confirm your password');

/**
 * UsernamePassword component renders email and password fields.
 * Fully accessible with labels and aria attributes.
 */
const UsernamePassword: React.FC<UsernamePasswordProps> = ({
	setFieldValue,
	emailDataTestid,
	passwordDataTestid,
	passwordConfirmationDataTestid,
	isSignUp = false,
}) => {
	return (
		<>
			<EmailField
				emailDataTestid={emailDataTestid}
				setFieldValue={setFieldValue}
			/>

			<FormikInputWithLabel
				id="password"
				label="Password"
				name="password"
				type="password"
				placeholder="Enter your password"
				className="col xl-12"
				data-testid={passwordDataTestid}
				setFieldValue={setFieldValue}
				icon
				autoComplete={isSignUp ? "new-password" : "current-password"}
				aria-required="true"
				required
			/>

			{isSignUp && <FormikInputWithLabel
				id="passwordConfirmation"
				label="Confirm Password"
				name="password_confirmation"
				type="password"
				placeholder="Confirm your password"
				className="col xl-12"
				data-testid={passwordConfirmationDataTestid}
				setFieldValue={setFieldValue}
				icon
				autoComplete={"new-password"}
				aria-required="true"
				required
			/>}
		</>
	);
};

export default UsernamePassword;
